/* istanbul ignore file */
import React, { Suspense } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import { BrightspotProvider } from '@fs/zion-cms'
import { appPath, sentryDSN, mergeSentryConfig } from '@fs/zion-config'
import zionDebug from '@fs/zion-debug'
import { NoticeLoading } from '@fs/zion-icon'
import { setAppLocales, essentialLocales } from '@fs/zion-locale'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { Router } from '@fs/zion-router'
import { AppLayout } from '@fs/zion-ui'
import * as Sentry from '@sentry/browser'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import App from './App'
import GlobalAppStyles from './components/GlobalAppStyles'
import useApac from './hooks/useApac'
import ApacProvider from './providers/ApacProvider'
import * as serviceWorkerRegistration from './serviceWorkerRegistration'
import constants from './utils/constants'
import './locales'

const debug = zionDebug('apac:index')

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    mergeSentryConfig({
      dsn: sentryDSN,
    })
  )
}

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''
// If the base is "/korea", get the value "korea" as the basePath for determining which app to render.
const splitBasePath = base.split('/')
const basePath = splitBasePath[splitBasePath.length - 1] || window?.location?.pathname?.split('/')?.[1] || ''

debug(`base: ${base}`)
debug(`basePath: ${basePath}`)

const queryClient = new QueryClient()
const sites = [...constants.SITES, 'playground']
const site = sites.find((s) => s === basePath)

// add any app logic here, languages and translations are configured and available here
const FrontierWrapper = () => {
  setAppLocales(essentialLocales)

  return (
    <Router basename={base}>
      <QueryClientProvider client={queryClient}>
        <ApacProvider site={site} basePath={basePath}>
          <AppSetup />
        </ApacProvider>
      </QueryClientProvider>
    </Router>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = () => (
  <React.StrictMode>
    <Suspense fallback={<NoticeLoading />}>
      <BrightspotProvider sites={sites} hasCustomContent>
        <FrontierBaseProviders mockFlags={mockFlags} mockActivities={mockActivities}>
          <FrontierWrapper />
        </FrontierBaseProviders>
      </BrightspotProvider>
    </Suspense>
  </React.StrictMode>
)

const AppSetup = () => {
  const { hideFooter, hideHeader } = useApac()

  return (
    <Root analytics footer={{ hidden: hideFooter, external: true }} header={{ hidden: hideHeader, external: true }}>
      <AppLayout fullWidth>
        <GlobalAppStyles>
          <App />
        </GlobalAppStyles>
      </AppLayout>
    </Root>
  )
}

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister()
